import { Inbox, Users, MessageSquare, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { PageShell } from "./page-shell";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/common/empty-state";
import { useAuth } from "@/hooks/use-auth";
import { useHandoffs, useLeads } from "@/lib/queries";
import { formatRelative } from "@/lib/utils";

export function DashboardPage() {
  const { session } = useAuth();
  const { data: leads, isLoading: leadsLoading } = useLeads();
  const { data: handoffs, isLoading: handoffsLoading } = useHandoffs();
  const greeting = session?.full_name?.split(" ")[0] || session?.username || "there";

  const handoffList = Array.isArray(handoffs) ? handoffs : [];
  const openHandoffs = handoffList.filter((h) => {
    const s = (h.status || "").toLowerCase();
    return s === "open" || s === "pending" || !s;
  });
  const recentLeads = (Array.isArray(leads) ? leads : []).slice(0, 6);

  return (
    <PageShell
      title={`Welcome back, ${greeting}.`}
      description="Today at a glance. Click anything to jump in."
    >
      <div className="grid gap-3 md:grid-cols-3">
        <SummaryRow
          to="/handoffs"
          icon={Inbox}
          title="Handoffs waiting"
          loading={handoffsLoading}
          value={openHandoffs.length}
          hint={openHandoffs.length === 0 ? "Inbox clear" : "Awaiting human reply"}
        />
        <SummaryRow
          to="/leads"
          icon={Users}
          title="Leads tracked"
          loading={leadsLoading}
          value={leads?.length ?? 0}
          hint="Total students the bot knows"
        />
        <SummaryRow
          to="/chat"
          icon={MessageSquare}
          title="Bot tester"
          loading={false}
          value={null}
          hint="Send test prompts and review responses"
        />
      </div>

      <div className="grid gap-5 lg:grid-cols-[1.4fr,1fr]">
        <section className="rounded-lg border border-line bg-surface">
          <header className="flex items-center justify-between border-b border-line px-5 py-3">
            <h3 className="text-md font-semibold text-ink">Recent leads</h3>
            <Link
              to="/leads"
              className="inline-flex items-center gap-1 text-sm text-accent hover:text-accent-strong"
            >
              View all <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </header>
          <div className="divide-y divide-line">
            {leadsLoading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="p-4">
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="mt-1.5 h-3 w-32" />
                </div>
              ))
            ) : recentLeads.length === 0 ? (
              <EmptyState
                icon={Users}
                title="No leads yet"
                description="As soon as the bot qualifies a student, they will appear here."
              />
            ) : (
              recentLeads.map((lead) => (
                <Link
                  key={lead.id}
                  to="/leads"
                  className="flex items-center justify-between px-5 py-3 transition-colors hover:bg-surface-muted"
                >
                  <div className="min-w-0">
                    <div className="font-medium text-ink truncate">
                      {lead.full_name || "Unnamed lead"}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-ink-muted">
                      {lead.phone && <span className="font-mono">{lead.phone}</span>}
                      {lead.program && <Badge tone="outline">{lead.program}</Badge>}
                    </div>
                  </div>
                  <span className="text-xs text-ink-faint">{formatRelative(lead.updated_at)}</span>
                </Link>
              ))
            )}
          </div>
        </section>

        <section className="rounded-lg border border-line bg-surface">
          <header className="flex items-center justify-between border-b border-line px-5 py-3">
            <h3 className="text-md font-semibold text-ink">Open handoffs</h3>
            <Link
              to="/handoffs"
              className="inline-flex items-center gap-1 text-sm text-accent hover:text-accent-strong"
            >
              Queue <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </header>
          <div className="divide-y divide-line">
            {handoffsLoading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="p-4">
                  <Skeleton className="h-4 w-56" />
                </div>
              ))
            ) : openHandoffs.length === 0 ? (
              <EmptyState
                icon={Inbox}
                title="Inbox clear"
                description="The bot is handling things on its own. Nice."
              />
            ) : (
              openHandoffs.slice(0, 8).map((h) => (
                <Link
                  key={h.id}
                  to="/handoffs"
                  className="flex flex-col gap-1 px-5 py-3 transition-colors hover:bg-surface-muted"
                >
                  <div className="font-medium text-ink truncate">
                    {h.full_name || "Handoff"}
                  </div>
                  <div className="text-xs text-ink-muted truncate">
                    {h.reason || "—"} · {formatRelative(h.created_at)}
                  </div>
                </Link>
              ))
            )}
          </div>
        </section>
      </div>
    </PageShell>
  );
}

function SummaryRow({
  to,
  icon: Icon,
  title,
  loading,
  value,
  hint,
}: {
  to: string;
  icon: typeof Inbox;
  title: string;
  loading: boolean;
  value: number | null;
  hint: string;
}) {
  return (
    <Link
      to={to}
      className="group flex items-center gap-4 rounded-lg border border-line bg-surface p-4 transition-colors hover:bg-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
    >
      <div className="grid h-10 w-10 place-items-center rounded-md bg-surface-muted text-ink-muted group-hover:text-accent">
        <Icon className="h-4 w-4" strokeWidth={1.75} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-xs uppercase tracking-wider text-ink-faint">{title}</div>
        <div className="flex items-baseline gap-2">
          {loading ? (
            <Skeleton className="h-5 w-12" />
          ) : value !== null ? (
            <span className="text-xl font-semibold text-ink tabular">{value}</span>
          ) : (
            <span className="text-sm font-medium text-ink">Open</span>
          )}
          <span className="text-xs text-ink-muted">{hint}</span>
        </div>
      </div>
      <ArrowRight className="h-4 w-4 text-ink-faint transition-colors group-hover:text-accent" />
    </Link>
  );
}
